import { gql } from '@apollo/client';
import { useNavigation } from '@react-navigation/native';
import { Button, Meta, theme } from 'aimo-ui';
import { showToast } from 'components/common/CustomToast';
import AccountContext from 'context/AccountContext';
import { addMonths, format, isValid } from 'date-fns';
import useUpdateSalesOrder from 'hooks/useUpdateSalesOrder';
import React, { useContext, useEffect, useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { usePermitStore } from 'stores/permitStore';
import { StyledWrapper } from 'styles/ContainerStyles';
import { ServicesNavProps } from 'types/navigation';
import { evictFromCache, updateCache } from 'utils/cacheUtils';
import { isSalesOrderWaiting } from 'utils/permitUtils';
import { testIdentifiers } from 'utils/testIdentifiers';
import { getRemainingDays } from 'utils/timeUtils';
import { useUiStore } from 'stores/uiStore';

const PAYMENT_TRANSACTION_THRESHOLD = 3;
const ConfirmCancelPermitModalContent: React.FC = () => {
  const { t } = useTranslation();
  const permitId = usePermitStore((state) => state.selectedPermit);
  const {
    customer: { permits },
  } = useContext(AccountContext);
  const { goBack, canGoBack } = useNavigation<ServicesNavProps>();

  const selectedSOrder = permits?.find(
    (sOrder) => sOrder?.orderLineIdWithVersion === permitId
  );
  const salesOrderId = selectedSOrder?.id;

  const { orderLines } = selectedSOrder ?? {};
  const orderLine = orderLines?.[0];

  const { validity, charge } = orderLine ?? {};

  const { validFrom } = validity ?? {};

  const isWaiting =
    isSalesOrderWaiting(selectedSOrder) ||
    selectedSOrder?.orderStatus === 'WAITING_FOR_CSR_APPROVAL';

  const lastValidDay = charge?.validity?.validTo ?? validity?.validTo;
  const validUntil = useMemo(() => {
    if (lastValidDay) {
      const date = new Date(lastValidDay);
      if (isValid(date)) {
        return date;
      }
    }
    const from = validFrom ? new Date(validFrom) : new Date();
    return addMonths(isValid(from) ? from : new Date(), 1);
  }, [lastValidDay, validFrom]);
  const remainingDays = getRemainingDays(validUntil);
  const endDateFormatted = format(validUntil, 'd.M.y');
  const showAdditionalInfo = remainingDays <= PAYMENT_TRANSACTION_THRESHOLD;
  const confirmDisableRenewalOnly = usePermitStore(
    (state) => state.confirmDisableRenewalOnly
  );
  const showDisableRenewalDialog = usePermitStore(
    (state) => state.showDisableRenewalDialog
  );
  const showDisableRenewalCopy =
    confirmDisableRenewalOnly || showDisableRenewalDialog;
  const cancelPermitDescription = isWaiting
    ? t('services.cancelPermitDescription')
    : `${t('services.cancelPermitDescription')} ${t(
        'services.cancelPermitDescriptionRemaining',
        { date: endDateFormatted }
      )}`;
  const description = showDisableRenewalCopy
    ? t('permit.disableAutomaticRenewalDescription', {
        date: endDateFormatted,
      })
    : cancelPermitDescription;

  const { updateSalesOrder, loading } = useUpdateSalesOrder({
    customUpdate: (cache, { data: updateData }) => {
      if (updateData?.updateSalesOrder?.orderStatus === 'EXPIRED') {
        evictFromCache(cache, {
          __typename: 'UnifiedPermit',
          id: salesOrderId,
          orderLineIdWithVersion: selectedSOrder?.orderLineIdWithVersion,
        });
        if (canGoBack()) {
          goBack();
        }
        showToast(`${t('success.permitCancelled')}`, 'success');
      } else {
        updateCache(
          cache,
          {
            __typename: 'UnifiedPermit',
            id: salesOrderId,
            orderLineIdWithVersion: selectedSOrder?.orderLineIdWithVersion,
          },
          {
            fragment: gql`
              fragment UnifiedPermitCancelFragment on UnifiedPermit {
                renewal
                orderStatus
              }
            `,
            data: {
              renewal: updateData?.updateSalesOrder?.renewal,
              orderStatus: updateData?.updateSalesOrder?.orderStatus,
            },
          }
        );
      }
    },
    customOnCompleted: () => {
      useUiStore.getState().setSheetModal('');
    },
    customOnError: () => {
      showToast('error.permitCancelled', 'error');
    },
  });

  useEffect(() => {
    return () => {
      // StrictMode remounts this while the sheet is still open; don't wipe flags then.
      if (useUiStore.getState().sheetModal === 'terminatePermit') {
        return;
      }
      usePermitStore.getState().setSelectedPermit(undefined);
      usePermitStore.getState().setConfirmDisableRenewalOnly(false);
      usePermitStore.getState().setShowDisableRenewalDialog(false);
    };
  }, []);

  const handleConfirmCancel = () => {
    if (usePermitStore.getState().confirmDisableRenewalOnly) {
      usePermitStore.getState().setConfirmDisableRenewalAction('confirm');
      useUiStore.getState().setSheetModal('');
      return;
    }
    const action = isWaiting ? 'CANCEL' : 'TERMINATE';
    salesOrderId &&
      updateSalesOrder({
        variables: {
          input: {
            salesOrderId: salesOrderId,
            updateType: action,
            renewal: false,
          },
        },
      });
    usePermitStore.getState().setWasPermitTerminated(action === 'TERMINATE');
  };
  return (
    <StyledWrapper mb={0}>
      <StyledWrapper center mb={theme.normalising.heightPixel(20)}>
        <Meta
          center
          color={theme.colors.gray300}
          testID="confirm-cancel-permit-description">
          {description}
        </Meta>
      </StyledWrapper>
      <StyledWrapper center>
        {showAdditionalInfo && (
          <Meta center color={theme.colors.gray300}>
            {t('services.cancelPermitAdditionalDescription')}
          </Meta>
        )}
      </StyledWrapper>
      <Button
        {...testIdentifiers('parking-history-apply-filter')}
        text={t('button.continue')}
        size="l"
        buttonType="secondary"
        onPress={handleConfirmCancel}
        disabled={loading}
        loading={loading}
      />
    </StyledWrapper>
  );
};

export default ConfirmCancelPermitModalContent;
